import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import LiveEditing from './Components/LiveEditing';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import BookingForm from './Components/BookingForm';
import Navigation from './Components/Navigation';
import CookieBanner from './Components/CookieBanner';
import HomeSearch from './Components/HomeSearch';
import HomeSearchResults from './Components/HomeSearchResults';
import CategoryItemViewer from './Components/CategoryItemViewer';
import SearchItemViewer from './Components/SearchItemViewer';
import StoreSmallItemListContainer from './Components/store/StoreSmallItemList.container';
import LensSpecification from './Components/ProductPage/LensSpecificationSelection';
import MyDetailsPanelStores from './Components/MyPage/MyDetailsPanelStores';
import ProductQuickSearch from './Components/ProductSearch/ProductQuickSearch';
import ProductSearchViewer from './Components/ProductSearch/ProductSearchViewer';
import MyPageRecommendation from './Components/MyPage/MyPageRecommendation';
import LoginWithCode from './Components/LoginWithCode/LoginWithCode';
import GDPRApprovalForm from './Backend/Pages/Ecommerce/GDPRApprovalForm';
import { RegistrationContainer } from './Components/ECommerce/Registration/RegistrationContainer';
import { toggleDescription } from './Components/ToggleDescription';
import { scrollIntoView } from './Components/ScrollIntoView';
import ProductPage from './Components/ProductPage/ProductPage';
import MyPageRecommendationLastOrder from './Components/MyPage/MyPageRecommendationLastOrder';
import MyPageSubscription from './Components/MyPage/Subscription/MyPageSubscription';
import MyDetailsUpdateCard from './Components/MyPage/Subscription/MyDetailsUpdateCard';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
  app,
  preloadState,
  composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

window.onload = function () {
  toggleDescription();
  scrollIntoView();
};

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
  if (!store.asyncReducers[key]) {
    store.asyncReducers[key] = asyncReducer;
    store.replaceReducer(createReducer(store.asyncReducers));
  }
};

window.__litium = {
  ...window.__litium,
  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach((container) => {
    ReactDOM.unmountComponentAtNode(container);
  });
};

const bootstrapComponents = () => {
  if (document.getElementById('liveEditing')) {
    renderReact(
        <Provider store={store}>
            <LiveEditing />
        </Provider>,
        document.getElementById('liveEditing')
    );
  }
  if (document.getElementById('backendEntryPoint')) {
    const BackendContainer = DynamicComponent({
      loader: () => import('./Backend/Backend.container'),
    });
    renderReact(
      <Provider store={store}>
          <BackendContainer />
      </Provider>,
      document.getElementById('backendEntryPoint')
    );
  }

  if (!document.getElementById('skip-cookie')) {
    renderReact(
      <CookieBanner />,
      document.body.appendChild(document.createElement('div'))
    );
  }

  if (document.getElementById('bookingMapContainer')) {
    const BookingContainer = DynamicComponent({
      loader: () => import('./Containers/Booking.container'),
    });

    const rootElement = document.getElementById('bookingMapContainer');
    const { domainName } = rootElement.dataset;
    const headerSlotHtml = rootElement.querySelector('.header-slot').innerHTML;
    renderReact(
      <Provider store={store}>
        <BookingContainer
          headerSlotHtml={headerSlotHtml}
          domainName={domainName}
        />
      </Provider>,
      document.getElementById('bookingMapContainer')
    );
  }
  if (document.querySelectorAll('store-details-map').length > 0) {
    const StoreDetailsMap = DynamicComponent({
      loader: () => import('./Components/StoreDetailsMap'),
    });
    Array.from(document.querySelectorAll('store-details-map')).forEach(
      (mapElement) => {
        const { lat, lng } = mapElement.dataset;
        const _lat = lat.replace(',', '.');
        const _lng = lng.replace(',', '.');
        renderReact(
          <StoreDetailsMap {...{ lat: _lat, lng: _lng }} />,
          mapElement
        );
      }
    );
  }
  if (document.getElementsByTagName('oembed')) {
    const oembeds = document.getElementsByTagName('oembed');
    [].slice.call(oembeds).forEach((oembed) => {
      let _value = oembed.outerHTML;
      _value = _value.replace(
        `<oembed url="https://www.youtube.com/watch?v=`,
        `<iframe width="100%" height="500px" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen src="https://www.youtube.com/embed/`
      );
      _value = _value.replace('</oembed>', '</iframe>');
      oembed.outerHTML = _value;
    });
  }
  if (document.getElementById('booking-form')) {
    const bookingForm = document.getElementById('booking-form');
    const { memberId, domainName, currentStore } = bookingForm.dataset;
    const isStorePage = true;
    const isUserRequest = true;
    renderReact(
      <Provider store={store}>
        <BookingForm
          {...{
            memberId,
            domainName,
            currentStore,
            isStorePage,
            isUserRequest,
          }}
        />
      </Provider>,
      document.getElementById('booking-form')
    );
  }
  if (document.getElementById('homeSearchBox')) {
    renderReact(
      <Provider store={store}>
        <HomeSearch />
      </Provider>,
      document.getElementById('homeSearchBox')
    );
  }
  if (document.getElementById('homeSearchResults')) {
    const { innerHTML } = document.getElementById('homeSearchResults');
    renderReact(
      <Provider store={store}>
        <HomeSearchResults placeholderHtml={innerHTML} />
      </Provider>,
      document.getElementById('homeSearchResults')
    );
  }

  if (document.getElementById('category-item-viewer')) {
    const isSearchPresent = true;
    const isSortingPresent = true;
    renderReact(
      <Provider store={store}>
        <CategoryItemViewer {...{ isSearchPresent, isSortingPresent }} />
      </Provider>,
      document.getElementById('category-item-viewer')
    );
  }

  if (document.getElementById('search-item-viewer')) {
    const searchItemViewer = document.getElementById('search-item-viewer');
    const {
      storeSearchResult,
      categories,
      title,
      onlystores,
      searchTerm,
      totalHits,
      articleSearchResult,
      articleSearchResultTitle,
    } = searchItemViewer.dataset;
    renderReact(
      <Provider store={store}>
        <SearchItemViewer
          {...{
            storeSearchResult,
            categories,
            title,
            onlystores,
            searchTerm,
            totalHits,
            articleSearchResult,
            articleSearchResultTitle,
          }}
        />
      </Provider>,
      document.getElementById('search-item-viewer')
    );
  }

  if (document.getElementById('navbar')) {
    const navbar = document.getElementById('navbar');
    const { directLinkList, isStoreWebsite } = navbar.dataset;
    let directLinkListConverted;
    const isStoreWebsiteConverted = isStoreWebsite === 'True';
    if (directLinkList) {
      directLinkListConverted = JSON.parse(directLinkList);
    }
    renderReact(
      <Provider store={store}>
        <Navigation
          {...{
            directLinkList: directLinkListConverted,
            isStoreWebsite: isStoreWebsiteConverted,
          }}
        />
      </Provider>,
      document.getElementById('navbar')
    );
  }

  if (document.getElementById('county-category-page')) {
    let storeList;
    let county;
    if (
      preloadState &&
      preloadState.countycategory &&
      preloadState.countycategory.payload
    ) {
      storeList = preloadState.countycategory.payload.storeList;
      county = preloadState.countycategory.payload.countyName;
    }
    renderReact(
      <Provider store={store}>
        <StoreSmallItemListContainer
          storeList={storeList}
          county={county}
          renderCounty={true}
        />
      </Provider>,
      document.getElementById('county-category-page')
    );
  }

  if (document.getElementById('concept-article-page')) {
    let storeList;
    if (
      preloadState &&
      preloadState.conceptarticle &&
      preloadState.conceptarticle.payload
    ) {
      storeList = preloadState.conceptarticle.payload;
    }
    renderReact(
      <Provider store={store}>
        <StoreSmallItemListContainer
          storeList={storeList}
          renderCounty={true}
        />
      </Provider>,
      document.getElementById('concept-article-page')
    );
  }

  if (document.getElementById('miniCart')) {
    renderReact(
      <Provider store={store}>
        <MiniCart />
      </Provider>,
      document.getElementById('miniCart')
    );
  }
  if (document.getElementById('quickSearch')) {
    renderReact(
      <Provider store={store}>
        <QuickSearch />
      </Provider>,
      document.getElementById('quickSearch')
    );
  }
  if (document.getElementById('facetedSearch')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearch />
      </Provider>,
      document.getElementById('facetedSearch')
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearchCompactContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact')
    );
  }
  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Components/PersonListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <PersonList />
      </Provider>,
      document.getElementById('myPagePersons')
    );
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Components/AddressListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses')
    );
  }
  if (document.getElementById('checkout')) {
    const Checkout = DynamicComponent({
      loader: () => import('./Components/Checkout'),
    });
    renderReact(
      <Provider store={store}>
        <Checkout />
      </Provider>,
      document.getElementById('checkout')
    );
  }
  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      store.injectReducer('lightboxImages', lightboxImages);
      const rootElement = document.getElementById('lightBoxImages');
      const images = Array.from(rootElement.querySelectorAll('template')).map(
        (img) => ({
          html: img.innerHTML,
          src: img.dataset.src,
        })
      );
      renderReact(
        <Provider store={store}>
          <LightboxImages images={images} />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    });
  }

  if (document.querySelectorAll('.slider').length > 0) {
    const Slider = DynamicComponent({
      loader: () => import('./Components/Slider'),
    });
    Array.from(document.querySelectorAll('.slider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('template')].map((block) => {
          return {
            html: block.innerHTML,
          };
        });
        if (values.length > 0) {
          renderReact(<Slider values={values} />, slider);
        }
      }
    );
  }

  if (document.getElementById('orderHistoryPage')) {
    const OrderList = DynamicComponent({
      loader: () => import('./Components/OrderHistoryListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <OrderList />
      </Provider>,
      document.getElementById('orderHistoryPage')
    );
  }

  if (document.getElementById('variant-lens-specifications')) {
    const button = document.querySelector('buy-button');
    const {
      articleNumber,
      quantityFieldId,
      href,
      cssClass,
      label,
    } = button.dataset;

    const variantLensSpecificationElem = document.getElementById(
      'variant-lens-specifications'
    );
    const {
      currentvariant,
      numberofpackets,
      baseprice,
      notavailable,
      addrange,
    } = variantLensSpecificationElem.dataset;
    renderReact(
      <Provider store={store}>
        <LensSpecification
          currentVariantId={currentvariant}
          numberOfPackets={JSON.parse(numberofpackets)}
          basePrice={baseprice}
          label={label}
          articleNumber={articleNumber}
          quantityFieldId={quantityFieldId}
          href={href}
          cssClass={cssClass}
          notAvailable={notavailable}
          isAddingRecommendations={false}
          ></LensSpecification>
      </Provider>,
      document.getElementById('variant-lens-specifications')
    );
  }

  if (document.getElementById('mydetailsstores')) {
    const storesElem = document.getElementById('mydetailsstores');
    const { selectedstore, validmodelstate, customerid, clinical } = storesElem.dataset;

    renderReact(
      <Provider store={store}>
        <MyDetailsPanelStores
          selectedStore={selectedstore}
          validModelState={validmodelstate}
          customerId={customerid}
          hasSubscriptionAndClinicalPartActive={clinical}
        ></MyDetailsPanelStores>
      </Provider>,
      document.getElementById('mydetailsstores')
    );
  }
  if (document.querySelectorAll('.mypage-recommendations')) {
    document.querySelectorAll('.mypage-recommendations').forEach((x) => {
      const {
        customerrecommendations,
        myrecommendationcount,
        ismyrecommendation,
        checkoutUrl,
        subscriptionexists
      } = x.dataset;
      renderReact(
        <Provider store={store}>
          <MyPageRecommendation
            customerRecommendation={customerrecommendations}
            myRecommendationCount={myrecommendationcount}
            isMyRecommendation={ismyrecommendation}
            checkoutUrl={checkoutUrl}
            subscriptionExists={subscriptionexists === "True"}
          ></MyPageRecommendation>
        </Provider>,
        x
      );
    });
  }

  if (document.querySelectorAll('.mypage-recommendations-last-order')) {
    document.querySelectorAll('.mypage-recommendations-last-order').forEach((x) => {
      const {
        customerrecommendations,
        myrecommendationcount,
        ismyrecommendation,
        checkoutUrl,
        subscriptionexists,
        totalprice,        
      } = x.dataset;
      renderReact(
        <Provider store={store}>
          <MyPageRecommendationLastOrder
            customerRecommendation={customerrecommendations}
            myRecommendationCount={myrecommendationcount}
            isMyRecommendation={ismyrecommendation}
            checkoutUrl={checkoutUrl}
            subscriptionExists={subscriptionexists === "True"}
            totalPrice={totalprice}            
          ></MyPageRecommendationLastOrder>
        </Provider>,
        x
      );
    });
  }

  if (document.getElementById('productQuickSearch')) {
    renderReact(
      <Provider store={store}>
        <ProductQuickSearch />
      </Provider>,
      document.getElementById('productQuickSearch')
    );
  }
  if (document.getElementById('product-search-item-viewer')) {
    const resultViewerElem = document.getElementById(
      'product-search-item-viewer'
    );
    const {
      results,
      pagination,
      sortkey,
      sortdirection,
    } = resultViewerElem.dataset;

    renderReact(
      <Provider store={store}>
        <ProductSearchViewer
          Results={results}
          Pagination={pagination}
          sortKey={sortkey}
          sortDirection={sortdirection}
        />
      </Provider>,
      document.getElementById('product-search-item-viewer')
    );
  }
  if (document.getElementById('loginWithCode')) {
    const loginWithCodeElem = document.getElementById('loginWithCode');
    const { helplink, redirectlink, recaptchasitekey } = loginWithCodeElem.dataset;

    renderReact(
      <Provider store={store}>
        <LoginWithCode HelpLink={helplink} RedirectLink={redirectlink} RecaptchaSiteKey={recaptchasitekey} />
      </Provider>,
      document.getElementById('loginWithCode')
    );
  }

  if (document.getElementById('gdpr-approval-form')) {
    const gdprform = document.getElementById('gdpr-approval-form');
    const { phonenumber } = gdprform.dataset;

    renderReact(
      <Provider store={store}>
        <GDPRApprovalForm phoneNumber={phonenumber} />
      </Provider>,
      document.getElementById('gdpr-approval-form')
    );
  }

  if (document.getElementById('registration-container')) {
    const registerForm = document.getElementById('registration-container');
    const { redirecturl, phonenumber,optician } = registerForm.dataset;

    renderReact(
      <Provider store={store}>
        <RegistrationContainer redirectUrl={redirecturl} phoneNumber={phonenumber}  optician={optician} />
      </Provider>,
      document.getElementById('registration-container')
    );
  }

  if (document.getElementById('product-page')) {
    const productElem = document.getElementById('product-page');
    const { variantpackagesizes, variant,similarproducts, checkoutpage } = productElem.dataset;

    renderReact(
      <Provider store={store}>
        <ProductPage variantPackageSizes={variantpackagesizes} variant={variant} similarProducts={similarproducts} checkoutPage={checkoutpage}/>
      </Provider>,
      document.getElementById('product-page')
    );
  }

  if (document.querySelectorAll('.mypage-subscription')) {
    document.querySelectorAll('.mypage-subscription').forEach((x) => {      
      const {
        customersubscription,  
        upcomingsubscription,    
        allpaymentsdone,  
      } = x.dataset;
      renderReact(
        <Provider store={store}>
          <MyPageSubscription
            customerSubscription={customersubscription} upcomingSubscription={upcomingsubscription} allPaymentsDone={allpaymentsdone}          
          ></MyPageSubscription>
        </Provider>,
        x
      );
    });
  }

  if (document.getElementById('mydetails-updatecard')) {             
      renderReact(
        <Provider store={store}>
          <MyDetailsUpdateCard                     
          ></MyDetailsUpdateCard>
        </Provider>,
        document.getElementById('mydetails-updatecard')
      );    
  }
};

bootstrapComponents();
